import { simulateLatency, type ApiContext } from '../shared';
import { STUB_SERVICE_ACCOUNTS } from './stubData';
import type {
  ServiceAccount,
  ServiceAccountApiResponse,
  CreateServiceAccountRequest,
  UpdateServiceAccountRequest,
  ListServiceAccountsParams,
  ListServiceAccountsResponse,
  RotatePasswordRequest,
  RotatePasswordResponse,
  EnableServiceAccountResponse,
  DisableServiceAccountResponse,
} from './types';
import { transformServiceAccount } from './types';
import { apiClient } from '@admin-ui-root/src/app/lib/apiClient';
import { fileDeliveryApiUrl } from '@admin-ui-root/src/app/pages/FileDelivery/api/api';

export const listServiceAccounts = async (
  ctx: ApiContext,
  params?: ListServiceAccountsParams,
): Promise<ListServiceAccountsResponse> => {
  const api = apiClient();

  // Build query parameters
  const searchParams = new URLSearchParams();
  if (params?.pageSize) searchParams.append('pageSize', params.pageSize.toString());
  if (params?.pageToken) searchParams.append('pageToken', params.pageToken);
  if (params?.filter) searchParams.append('filter', params.filter);
  if (params?.orderBy) searchParams.append('orderBy', params.orderBy);

  const queryString = searchParams.toString();
  const url = queryString ? `serviceAccounts?${queryString}` : 'serviceAccounts';

  const response = await api
    .get(fileDeliveryApiUrl(url), {
      headers: {
        'x-fds-profile': ctx.serviceAccount,
        'x-c2fo-dc': ctx.datacenter,
      },
    })
    .json<unknown>();

  // Handle empty object response when no results are found
  if (!response || Object.keys(response).length === 0) {
    return {
      serviceAccounts: [],
      totalSize: 0,
    };
  }

  return response as ListServiceAccountsResponse;
};

export const getServiceAccount = async (ctx: ApiContext, name: string): Promise<ServiceAccount | null> => {
  const api = apiClient();

  try {
    const response = await api
      .get(fileDeliveryApiUrl(name), {
        headers: {
          'x-fds-profile': ctx.serviceAccount,
          'x-c2fo-dc': ctx.datacenter,
        },
      })
      .json<{ serviceAccount: ServiceAccountApiResponse }>();

    return transformServiceAccount(response.serviceAccount);
  } catch (error) {
    return null;
  }
};

export const createServiceAccount = async (
  ctx: ApiContext,
  _request: CreateServiceAccountRequest,
): Promise<ServiceAccount> => {
  // TODO: Replace with real API call when available
  // No-op implementation - returns a mock service account
  await simulateLatency();

  const now = new Date().toISOString();
  const newServiceAccount: ServiceAccountApiResponse = {
    ..._request.serviceAccount,
    name: `serviceAccounts/${_request.serviceAccount.username}`,
    uid: crypto.randomUUID(),
    createTime: now,
    updateTime: now,
  };

  return transformServiceAccount(newServiceAccount);
};

export const updateServiceAccount = async (
  ctx: ApiContext,
  _request: UpdateServiceAccountRequest,
): Promise<ServiceAccount> => {
  // TODO: Replace with real API call when available
  // No-op implementation
  await simulateLatency();

  const existing = STUB_SERVICE_ACCOUNTS.find((sa) => sa.name === _request.serviceAccount.name);
  if (!existing) {
    throw new Error(`Service account not found: ${_request.serviceAccount.name}`);
  }

  return transformServiceAccount({
    ...existing,
    ..._request.serviceAccount,
    updateTime: new Date().toISOString(),
  });
};

export const deleteServiceAccount = async (_ctx: ApiContext, _name: string): Promise<void> => {
  // TODO: Replace with real API call when available
  // No-op implementation
  await simulateLatency();
};

export const rotateServiceAccountPassword = async (
  _ctx: ApiContext,
  _request: RotatePasswordRequest,
): Promise<RotatePasswordResponse> => {
  // TODO: Replace with real API call when available
  // TEMP: Force error for demo
  await simulateLatency();
  return Promise.reject({
    status: 500,
    message: 'Failed to rotate password: Service unavailable',
  });

  // return { success: true };
};

export const enableServiceAccount = async (_ctx: ApiContext, name: string): Promise<EnableServiceAccountResponse> => {
  // TODO: Replace with real API call when available
  // No-op implementation
  await simulateLatency();

  const existing = STUB_SERVICE_ACCOUNTS.find((sa) => sa.name === name || sa.uid === name);
  if (!existing) {
    throw new Error(`Service account not found: ${name}`);
  }

  return {
    serviceAccount: transformServiceAccount({
      ...existing,
      enabled: true,
      updateTime: new Date().toISOString(),
    }),
  };
};

export const disableServiceAccount = async (_ctx: ApiContext, name: string): Promise<DisableServiceAccountResponse> => {
  // TODO: Replace with real API call when available
  // No-op implementation
  await simulateLatency();

  const existing = STUB_SERVICE_ACCOUNTS.find((sa) => sa.name === name || sa.uid === name);
  if (!existing) {
    throw new Error(`Service account not found: ${name}`);
  }

  return {
    serviceAccount: transformServiceAccount({
      ...existing,
      enabled: false,
      updateTime: new Date().toISOString(),
    }),
  };
};
